'use client'

import Loader from '@/app/components/loader'
import { cn } from '@/util/cn'
import CreationToolCutOutMode from 'creation/src/creation-tools/types/cut-out-mode'
import CreationToolError from 'creation/src/creation-tools/types/error'
import CreationToolOutputType from 'creation/src/creation-tools/types/output-type'
import CreationToolStep from 'creation/src/creation-tools/types/steps'
import { CreationToolFrame } from 'creation/src/creation-tools/types'
import { isAGoodSticker } from 'creation/src/creation-tools/utils/image-data-utils'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBeforeUnload } from 'react-use'
import Breadcrumbs from '../../breadcrumbs'
import ContinueButton from '../../continue-button'
import {
    columnBlock,
    columnBlockFirstChild,
    containerBlock,
    splitBlock,
    splitBlockColumn,
    stickerBlock,
    wrapperBlock,
} from '../../presentation/blocks'
import { text, title } from '../../presentation/typography'
import { disposeUnreachableFrames } from '../../store/dispose'
import { useCreationStore } from '../../store/provider'
import { useCreationNextStep } from '../../use-creation-next-step'
import WelcomeErrorModal from '../../welcome/error-modal'
import CutOutButtons from './cut-out-buttons'
import CutOutCanvas from './cut-out-canvas'
import { useRemovingBackgroundMessage } from './removing-background-message'
import { CutOutContext, type Path } from './cut-out-context'
import CutOutNavigation from './cut-out-navigation'
import Instructions from './instructions'
import { getStickerFromCanvas, getStickerFromSource } from './sticker'
import { getFileImageElement } from './utils'

const STEP = CreationToolStep.CutOut
// The store doesn't expose basePath (see video-trim-buttons.tsx) — this step
// only ever mounts under the GIF Maker.
const BASE_PATH = '/create/gifmaker'

/**
 * Ported from legacy components/cut-out/index.tsx:35-249. Assembles the
 * cut-out screen — canvas, tool grid, undo/reset navigation, instructions,
 * breadcrumbs, continue — and owns both source paths (still image edited
 * here, animated source skipped straight through background removal to the
 * next step).
 */
const CutOutStep = () => {
    const router = useRouter()
    const sources = useCreationStore((state) => state.sources)
    const steps = useCreationStore((state) => state.steps)
    const setFiles = useCreationStore((state) => state.setFiles)
    const userRestrictionAccess = useCreationStore((state) => state.userRestrictionAccess)
    // Read for disposal only: the unmount cleanup must not release canvases
    // the store is still holding as `stepFrames` — see the cleanup below.
    const stepFrames = useCreationStore((state) => state.stepFrames)

    const [isMagicWandLoading, setIsMagicWandLoading] = useState<boolean>(false)
    const [mode, setMode] = useState<CreationToolCutOutMode>(CreationToolCutOutMode.Lasso)
    const [modeInstructionPreview, setModeInstructionPreview] = useState<CreationToolCutOutMode>()
    const [undoStepCount, setUndoStepCount] = useState<number>(0)
    const [isReady, setIsReady] = useState<boolean>(false)
    const [editCanvas, setEditCanvas] = useState<CreationToolFrame[]>([])
    const paths = useRef<Path[]>([])
    const [isResetEnable, setIsResetEnable] = useState<boolean>(false)
    const [eraseSize, setEraseSize] = useState<number>(0.5)
    const [brushSize, setBrushSize] = useState<number>(0.5)
    const [isBrushEnabled, setIsBrushEnabled] = useState<boolean>(false)
    const [sourceImageUrl, setSourceImageUrl] = useState<HTMLImageElement>()
    const [error, setError] = useState<CreationToolError>()

    const nextStep = useCreationNextStep(steps)
    const nextUrl = nextStep ? `${BASE_PATH}${CreationToolStep.route(nextStep)}` : ''

    const source = sources[0]
    // Legacy wraps this in `useMemo(..., [sources])` (cut-out/index.tsx:55-67)
    // so its identity is stable across renders. Restored here for the same
    // reason: `drawFrames` is a dependency of the effect below that calls
    // `checkIfIsTransparentEnoughToContinue`, and a fresh array identity every
    // render would re-fire that effect on every render, restarting the 500ms
    // debounce timer each time — undercutting the §12.3 debounce fix.
    const drawFrames: CreationToolFrame[] = useMemo(
        () =>
            source?.frames && source.frames.length > 0
                ? source.frames
                : source?.preview
                  ? [{ canvas: source.preview }]
                  : [],
        [sources]
    )

    // page reload warning
    useBeforeUnload(undoStepCount > 0 || isReady, `You'll lose all your beautiful work!`)

    const loadFirstEditCanvas = useCallback(() => {
        setIsResetEnable(false)
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d', { willReadFrequently: true })

        if (context && sources[0]) {
            const { width, height } = sources[0]

            canvas.width = width
            canvas.height = height

            context.drawImage(sources[0].preview, 0, 0)
            setEditCanvas([
                {
                    frameCount: 1,
                    canvas,
                },
            ])
        }
    }, [sources])

    // handle magic wand
    const handleMagicWand = async () => {
        if (sources.length > 0) {
            setIsMagicWandLoading(true)
            const currentSource = sources[0]
            const framesToRemoveBg = editCanvas.length > 0 ? editCanvas : currentSource.frames

            try {
                let stickers = await getStickerFromCanvas(framesToRemoveBg)
                const { canvas } = stickers[0]
                const context = canvas.getContext('2d')
                const imageData = context?.getImageData(0, 0, canvas.width, canvas.height)

                if (imageData) {
                    // check if it's a good sticker to continue
                    if (!isAGoodSticker(imageData)) {
                        // If not, use v2 to get new sticker
                        stickers = await getStickerFromCanvas(framesToRemoveBg, 'v2')
                    }

                    // version
                    stickers.length > 0 &&
                        setEditCanvas((prev) => {
                            prev[0].canvas = stickers[0].canvas
                            return [...prev]
                        })
                }

                setIsResetEnable(true)
                setIsBrushEnabled(true)
                setIsMagicWandLoading(false)
            } catch (err) {
                setError(CreationToolError.RemoveBackgroundFailed)
            }
        }
    }

    const resetCanvas = () => loadFirstEditCanvas()

    const setBrushEnabled = () => setIsBrushEnabled(true)

    // The tools call `checkIfIsTransparentEnoughToContinue` from inside a
    // `draw` callback that closes over its first render (lasso.tsx, pen.tsx),
    // so this must be a stable function reading fresh state through refs
    // rather than closing over `editCanvas`/`drawFrames` directly.
    const editCanvasRef = useRef(editCanvas)
    const drawFramesRef = useRef(drawFrames)

    useEffect(() => {
        editCanvasRef.current = editCanvas
    }, [editCanvas])

    useEffect(() => {
        drawFramesRef.current = drawFrames
    }, [drawFrames])

    const stepFramesRef = useRef(stepFrames)

    useEffect(() => {
        stepFramesRef.current = stepFrames
    }, [stepFrames])

    const transparencyTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

    // Legacy debounces this at 500ms but re-creates the debounced function
    // every render, so it never actually debounces — each call gets its own
    // timer and they all fire, each running a full-canvas getImageData and
    // per-pixel alpha scan. Spec §12.3. Consequence of fixing it: Continue
    // now enables 500ms after a drag ends rather than 500ms after it starts.
    const checkIfIsTransparentEnoughToContinue = useCallback(() => {
        clearTimeout(transparencyTimer.current)
        transparencyTimer.current = setTimeout(() => {
            const checkFrame = editCanvasRef.current.length ? editCanvasRef.current[0] : drawFramesRef.current[0]
            if (!checkFrame) {
                setIsReady(false)
                return
            }
            const { canvas } = checkFrame
            const context = canvas.getContext('2d', { willReadFrequently: true })
            const imageData = context?.getImageData(0, 0, canvas.width, canvas.height)
            setIsReady(!!(imageData && isAGoodSticker(imageData)))
        }, 500)
    }, [])

    useEffect(() => () => clearTimeout(transparencyTimer.current), [])

    const updatePaths = (path: Path[]) => {
        paths.current = path
        setIsResetEnable(true)
        checkIfIsTransparentEnoughToContinue()
    }

    const sendAnimatedSourceToNextStep = useCallback(async () => {
        if (nextStep && nextUrl) {
            const currentSource = sources[0]

            try {
                const sticker = await getStickerFromSource(currentSource)
                setFiles([sticker], CreationToolOutputType.Sticker)
                // `replace`, not `push` — legacy pushes (cut-out/index.tsx:161)
                // and leaves this screen in history, but for an animated source
                // this screen is a pass-through the user never interacts with:
                // it mounts, runs background removal, and leaves. A history
                // entry for it means Back returns here and the mount effect
                // runs the whole removal again — every request, from the start.
                // Spec §12.15.
                //
                // The still-image path keeps `push` (ContinueButton), because
                // there this screen is the editor: Back is a legitimate "go
                // adjust the cut-out", and nothing re-runs on the way in.
                router.replace(nextUrl)
            } catch (err) {
                setError(CreationToolError.RemoveBackgroundFailed)
            }
        }
    }, [nextStep, nextUrl, sources, setFiles, router])

    // Legacy re-enters this path when setFiles replaces `sources` (index.tsx:166,183)
    // and escapes only on router timing. Spec §4.3/§12.1. The ref latches before the
    // first await so a second effect run cannot start a second removal, and the
    // render-time guard keeps the tool UI from painting for a frame on the way out —
    // useLayoutEffect alone cannot prevent a paint under the App Router's async
    // navigation (verified for video-trim in spec 2b §18.0).
    const hasStartedRemoval = useRef(false)
    const shouldSkipToNext = sources[0]?.isAnimated

    useEffect(() => {
        if (!shouldSkipToNext || hasStartedRemoval.current || !nextStep || !nextUrl) return
        hasStartedRemoval.current = true
        void sendAnimatedSourceToNextStep()
    }, [shouldSkipToNext, nextStep, nextUrl, sendAnimatedSourceToNextStep])

    useEffect(() => {
        if (!source) return
        if (!source.isAnimated) {
            const loadCutOut = async () => {
                const imageElement = await getFileImageElement(source.source as File)
                setSourceImageUrl(imageElement)
            }

            if (editCanvas.length === 0) {
                loadFirstEditCanvas()
            }

            !sourceImageUrl && loadCutOut()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sources, loadFirstEditCanvas])

    useEffect(
        () => checkIfIsTransparentEnoughToContinue(),
        [drawFrames, editCanvas, checkIfIsTransparentEnoughToContinue]
    )

    // Spec §9 — editCanvas holds full-resolution canvases the creation store
    // does not own. Release them on unmount.
    //
    // But release only what the store is NOT still holding. continue-button's
    // onClick hands this SAME `editCanvas` array to `saveFrames`, which the
    // store keeps by reference as `stepFrames[step]` (create-store.ts:186) —
    // not a copy. Disposing the array wholesale zeroed those canvases, so the
    // store was left holding 0x0 backing stores. Nothing reads `stepFrames`
    // today, which is why it was invisible; 4b's Animate step is defined by
    // spec §4.2 as the first consumer of `stepFrames[Upload]` from this path
    // and would have received blank canvases on day one.
    useEffect(() => {
        return () => disposeUnreachableFrames(editCanvasRef.current, Object.values(stepFramesRef.current))
    }, [])

    // Before the early returns below — a hook cannot sit behind one.
    const removingBackgroundMessage = useRemovingBackgroundMessage(!!shouldSkipToNext && !error)

    if (shouldSkipToNext && !error) {
        // An animated source never populates `editCanvas` — it goes straight
        // through background removal on mount — so this covers that entire run,
        // which for video is one request per third of a second of footage.
        // Legacy shows a bare spinner for the duration. Spec §12.14.
        return (
            <div className={cn(wrapperBlock, 'gap-[20px]')}>
                <Loader />
                {/* Matches the error modal's title (welcome/error-modal.tsx:124)
                    so the step's two full-page states read as one family. */}
                <p role="status" className="m-0 text-center text-2xl font-black text-giphyWhite antialiased">
                    {removingBackgroundMessage}
                </p>
            </div>
        )
    }

    if (error) {
        return (
            <div className={wrapperBlock}>
                <WelcomeErrorModal
                    error={error}
                    userRestrictionAccess={userRestrictionAccess}
                    onGoBack={() => router.back()}
                    onChangeOutputType={() => {}}
                />
            </div>
        )
    }

    return (
        <CutOutContext.Provider
            value={{
                mode,
                editCanvas,
                modeInstructionPreview,
                undoStepCount,
                setMode,
                setModeInstructionPreview,
                setUndoStepCount,
                updatePaths,
                paths: paths.current,
                eraseSize,
                setEraseSize,
                brushSize,
                setBrushSize,
                sourceImageUrl,
                handleMagicWand,
                setBrushEnabled,
                isBrushEnabled,
                resetCanvas,
                setIsResetEnable,
                isResetEnable,
                checkIfIsTransparentEnoughToContinue,
            }}
        >
            {drawFrames.length > 0 && editCanvas.length > 0 ? (
                <div className={containerBlock}>
                    <div className={splitBlock}>
                        <div className={cn(splitBlockColumn, columnBlock)}>
                            <div className={columnBlockFirstChild}>
                                <CutOutNavigation />
                                <div className={cn(stickerBlock, 'relative')}>
                                    <CutOutCanvas isLoading={isMagicWandLoading} />
                                    <Instructions />
                                </div>
                            </div>
                            <Breadcrumbs />
                        </div>
                        <div className={cn(splitBlockColumn, columnBlock)}>
                            <div className={columnBlockFirstChild}>
                                <h1 className={title}>{CreationToolStep.title(STEP)}</h1>
                                <p className={text}>{CreationToolStep.description(STEP)}</p>
                                <CutOutButtons />
                            </div>
                            <ContinueButton
                                active={isReady}
                                frames={editCanvas.length ? editCanvas : drawFrames}
                                basePath={BASE_PATH}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <Loader />
            )}
        </CutOutContext.Provider>
    )
}

export default CutOutStep
